import { Building2, Users, Truck, Package, FileText, BarChart3, Fuel, TrendingUp, Clock, Shield, CheckCircle, ArrowRight, Zap, Target, Award, Globe, Lock, Sparkles, ChevronRight, Star, Smartphone, Camera, QrCode, ClipboardList, FileCheck, MapPin, Smile, Rocket, Monitor } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import StructuredData from '../components/StructuredData';
import { useTranslation, useLanguage } from '../i18n';

export default function Home() {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();
  return (
    <div className="min-h-screen bg-[#0E0A31] text-white overflow-x-hidden">
      <SEOHead
        title="Software Management Constructii Santier Flota Pontaj HR Centre Cost Achizitii | ControlMax Romania"
        description="Platforma completa management operational pentru firme constructii 10-700 angajati: software pontaj HR santier, GPS tracking flota, management achizitii stocuri, centre cost multi-proiecte, logistica transport, echipamente utilaje, documente contracte DMS cu AI OCR. Reducere costuri 30-42%, eficienta 40%. Solutie scalabila multi-santier Romania."
        keywords="software management constructii, platforma management operational, software firme constructii romania, gps tracking flota, pontaj hr, pontaj santier, management achizitii, management stocuri, centre cost, logistica transport, management echipamente utilaje, software dms documente, ai ocr facturi, management multi-proiecte, multi-santier, solutie scalabila firme mijlocii mari, software 10 angajati, software 50 angajati, software 100 angajati, software 200 angajati, software 300 angajati, software 500 angajati, software 700 angajati, digitalizare constructii romania, control operational, reducere costuri constructii, management santier, software management Romania"
        ogImage="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1200"
        canonicalUrl={`${window.location.origin}${window.location.pathname}`}
      />
      <StructuredData
        type="Organization"
        data={{
          name: "ControlMax",
          description: "Platformă pentru management operational constructii: GPS tracking flota, pontaj HR, logistica, achizitii, centre cost",
          url: window.location.origin,
          logo: `${window.location.origin}/grayscale_transparent_nobuffer.png`,
          telephone: "+40787683304",
          email: "contact@controlmax.ro",
          address: {
            "@type": "PostalAddress",
            addressCountry: "RO",
            addressLocality: "Romania"
          },
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "Sales",
            telephone: "+40787683304",
            areaServed: "RO",
            availableLanguage: ["Romanian", "English"]
          },
          sameAs: [
            "https://www.linkedin.com/company/controlmax",
            "https://www.facebook.com/controlmax"
          ]
        }}
      />
      <StructuredData
        type="WebSite"
        data={{
          name: "ControlMax",
          url: window.location.origin,
          inLanguage: "ro-RO"
        }}
      />
      <StructuredData
        type="SoftwareApplication"
        data={{
          name: "ControlMax",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web, iOS, Android",
          description: "Software management operational pentru firme de constructii: GPS tracking, pontaj HR, logistica, achizitii, centre cost",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "EUR",
            description: "Demo gratuit. Pret personalizat in functie de numarul de angajati."
          },
          featureList: "GPS Tracking, HR Pontaj, Logistica, Achizitii, Management Proiecte, Centre de Cost, Raportare Automata"
        }}
      />
      <StructuredData
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Acasa",
              item: window.location.origin
            }
          ]
        }}
      />
      <StructuredData
        type="FAQPage"
        data={{
          mainEntity: t.home.faq.questions?.slice(0, 5).map(faq => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.answer
            }
          })) || []
        }}
      />
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-gradient-to-br from-blue-600/20 via-purple-500/15 to-transparent rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-gradient-to-br from-violet-600/20 via-purple-500/15 to-transparent rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute bottom-0 left-1/3 w-[550px] h-[550px] bg-gradient-to-br from-purple-600/15 via-blue-500/10 to-transparent rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
      </div>

      <Navigation />

      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-fade-in">
              <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-purple-500/30 px-4 py-2 rounded-lg">
                <span className="text-sm font-medium bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">{t.home.hero.badge}</span>
              </div>

              <h1 className="text-5xl lg:text-6xl font-black leading-tight tracking-tight">
                <span className="text-white">
                  {t.home.hero.title}
                </span>
              </h1>

              <p className="text-xl text-slate-300 leading-relaxed">
                {t.home.hero.subtitle}
              </p>

              <div className="bg-[#1a1447]/50 border-l-2 border-purple-500 p-6 rounded-lg">
                <p className="text-base font-semibold text-slate-200 mb-3">{t.home.hero.challenges}</p>
                <ul className="space-y-2 text-slate-400 text-sm">
                  <li className="flex items-start">
                    <span className="text-slate-500 mr-2">•</span>
                    <span>{t.home.hero.challenge1}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-slate-500 mr-2">•</span>
                    <span>{t.home.hero.challenge2}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-slate-500 mr-2">•</span>
                    <span>{t.home.hero.challenge3}</span>
                  </li>
                </ul>
              </div>

              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Link to={getLocalizedPath('/calculator')}>
                    <button className="w-full group relative px-8 py-4 rounded-xl font-bold text-lg border-2 border-teal-400 bg-teal-500/10 hover:bg-teal-500 text-teal-400 hover:text-white shadow-lg hover:shadow-teal-500/50 transform hover:scale-[1.02] transition-all">
                      <span className="flex items-center justify-center">
                        {t.home.hero.ctaCalculator}
                        <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </button>
                  </Link>
                  <Link to={getLocalizedPath('/demo')}>
                    <button className="w-full group relative px-8 py-4 rounded-xl font-bold text-lg shadow-2xl hover:shadow-purple-500/50 transform hover:scale-[1.02] transition-all overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-500 to-violet-600 group-hover:from-violet-600 group-hover:via-purple-500 group-hover:to-blue-600 transition-all duration-500"></div>
                      <span className="relative flex items-center justify-center text-white">
                        {t.home.hero.ctaDemo}
                        <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </button>
                  </Link>
                </div>
                <p className="text-center text-sm text-slate-400">
                  {t.home.hero.features}
                </p>
                <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
                  <CheckCircle className="h-4 w-4 text-blue-500" />
                  <span>{t.home.hero.trustedBy}</span>
                </div>
              </div>

            </div>

            <div className="relative animate-fade-in-delayed">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-800">
                <img
                  src="https://images.pexels.com/photos/585419/pexels-photo-585419.jpeg?auto=compress&cs=tinysrgb&w=600&h=375&fit=crop"
                  srcSet="https://images.pexels.com/photos/585419/pexels-photo-585419.jpeg?auto=compress&cs=tinysrgb&w=400&h=250&fit=crop 400w, https://images.pexels.com/photos/585419/pexels-photo-585419.jpeg?auto=compress&cs=tinysrgb&w=600&h=375&fit=crop 600w, https://images.pexels.com/photos/585419/pexels-photo-585419.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop 800w, https://images.pexels.com/photos/585419/pexels-photo-585419.jpeg?auto=compress&cs=tinysrgb&w=1200&h=750&fit=crop 1200w"
                  sizes="(max-width: 768px) 100vw, 50vw"
                  alt="Echipă șantier"
                  className="w-full h-[500px] object-cover"
                  width={600}
                  height={375}
                  fetchPriority="high"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent"></div>
                <div className="absolute top-4 right-4 bg-emerald-500 text-white px-3 py-1 rounded-md text-xs font-medium flex items-center">
                  <div className="w-1.5 h-1.5 bg-white rounded-full mr-2"></div>
                  {t.home.hero.live}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <h3 className="text-2xl font-semibold mb-6">{t.home.stats.title}</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 backdrop-blur rounded-xl p-4 border border-purple-500/30 hover:scale-105 transition-transform">
                      <div className="text-3xl font-black bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-1">23%</div>
                      <div className="text-xs text-slate-300 font-semibold">{t.home.stats.costReduction}</div>
                    </div>
                    <div className="bg-gradient-to-br from-violet-600/20 to-blue-600/20 backdrop-blur rounded-xl p-4 border border-violet-500/30 hover:scale-105 transition-transform">
                      <div className="text-3xl font-black bg-gradient-to-r from-violet-400 to-blue-400 bg-clip-text text-transparent mb-1">30%</div>
                      <div className="text-xs text-slate-300 font-semibold">{t.home.stats.teamEfficiency}</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-600/20 to-violet-600/20 backdrop-blur rounded-xl p-4 border border-purple-500/30 hover:scale-105 transition-transform">
                      <div className="text-3xl font-black bg-gradient-to-r from-purple-400 to-violet-400 bg-clip-text text-transparent mb-1">41%</div>
                      <div className="text-xs text-slate-300 font-semibold">{t.home.stats.stockOptimization}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-purple-50/30 to-violet-50/40"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600/10 to-purple-600/10 border border-purple-600/20 px-4 py-2 rounded-lg mb-6">
              <span className="text-sm font-bold bg-gradient-to-r from-blue-700 to-purple-700 bg-clip-text text-transparent">{t.home.digital.title}</span>
            </div>
            <h2 className="text-4xl font-black mb-4 bg-gradient-to-r from-slate-900 via-purple-900 to-blue-900 bg-clip-text text-transparent">
              {t.home.digital.subtitle}
            </h2>
            <p className="text-lg text-slate-700 max-w-2xl mx-auto mb-8 font-medium">
              {t.home.digital.description}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-white/80 backdrop-blur rounded-2xl p-8 border border-slate-200 shadow-xl hover:shadow-2xl hover:border-purple-300 transition-all group">
              <div className="bg-gradient-to-br from-blue-500 to-purple-500 w-14 h-14 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <Users className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-3">{t.home.digital.timeTracking.title}</h3>
              <p className="text-slate-600 mb-4 font-medium">{t.home.digital.timeTracking.description}</p>
              <div className="flex items-center text-sm text-purple-600 font-semibold">
                <Clock className="h-4 w-4 mr-2" />
                {t.home.digital.timeTracking.badge}
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur rounded-2xl p-8 border border-slate-200 shadow-xl hover:shadow-2xl hover:border-violet-300 transition-all group">
              <div className="bg-gradient-to-br from-violet-500 to-purple-500 w-14 h-14 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <Truck className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-3">{t.home.digital.gpsTracking.title}</h3>
              <p className="text-slate-600 mb-4 font-medium">{t.home.digital.gpsTracking.description}</p>
              <div className="flex items-center text-sm text-violet-600 font-semibold">
                <CheckCircle className="h-4 w-4 mr-2" />
                {t.home.digital.gpsTracking.badge}
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur rounded-2xl p-8 border border-slate-200 shadow-xl hover:shadow-2xl hover:border-blue-300 transition-all group">
              <div className="bg-gradient-to-br from-blue-500 to-violet-500 w-14 h-14 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <Package className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-3">{t.home.digital.procurement.title}</h3>
              <p className="text-slate-600 mb-4 font-medium">{t.home.digital.procurement.description}</p>
              <div className="flex items-center text-sm text-blue-600 font-semibold">
                <BarChart3 className="h-4 w-4 mr-2" />
                {t.home.digital.procurement.badge}
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur rounded-2xl p-8 border border-slate-200 shadow-xl hover:shadow-2xl hover:border-purple-300 transition-all group">
              <div className="bg-gradient-to-br from-purple-500 to-violet-500 w-14 h-14 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <FileText className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-3">{t.home.digital.documents.title}</h3>
              <p className="text-slate-600 mb-4 font-medium">{t.home.digital.documents.description}</p>
              <div className="flex items-center text-sm text-purple-600 font-semibold">
                <Sparkles className="h-4 w-4 mr-2" />
                {t.home.digital.documents.badge}
              </div>
            </div>
          </div>

          <div className="text-center mb-16">
            <h2 className="text-3xl font-black mb-4 bg-gradient-to-r from-slate-900 via-purple-900 to-blue-900 bg-clip-text text-transparent">
              {t.home.digital.result.title}
            </h2>
            <p className="text-lg text-slate-700 max-w-2xl mx-auto font-medium">
              {t.home.digital.result.description}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white/80 backdrop-blur rounded-2xl p-8 border border-slate-200 shadow-xl hover:shadow-2xl hover:border-purple-300 transition-all group">
              <div className="bg-gradient-to-br from-blue-500 to-purple-500 w-14 h-14 rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform">
                <BarChart3 className="h-7 w-7 text-white" />
              </div>
              <div className="text-2xl font-black text-slate-900 mb-2">{t.home.digital.features.costCenters}</div>
              <p className="text-slate-600 font-medium">{t.home.digital.features.costCentersDesc}</p>
            </div>
            <div className="bg-white/80 backdrop-blur rounded-2xl p-8 border border-slate-200 shadow-xl hover:shadow-2xl hover:border-violet-300 transition-all group">
              <div className="bg-gradient-to-br from-violet-500 to-purple-500 w-14 h-14 rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform">
                <Clock className="h-7 w-7 text-white" />
              </div>
              <div className="text-2xl font-black text-slate-900 mb-2">{t.home.digital.features.instantReporting}</div>
              <p className="text-slate-600 font-medium">{t.home.digital.features.instantReportingDesc}</p>
            </div>
            <div className="bg-white/80 backdrop-blur rounded-2xl p-8 border border-slate-200 shadow-xl hover:shadow-2xl hover:border-blue-300 transition-all group">
              <div className="bg-gradient-to-br from-violet-500 to-blue-500 w-14 h-14 rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform">
                <Target className="h-7 w-7 text-white" />
              </div>
              <div className="text-2xl font-black text-slate-900 mb-2">{t.home.digital.features.fullControl}</div>
              <p className="text-slate-600 font-medium">{t.home.digital.features.fullControlDesc}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-[#1a1447]/50">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30 px-4 py-2 rounded-lg mb-6">
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">{t.home.dashboard.title}</span>
            </div>
            <h2 className="text-4xl font-bold mb-6 text-white">
              {t.home.dashboard.subtitle}
            </h2>
            <p className="text-lg text-slate-300 max-w-3xl mx-auto">
              {t.home.dashboard.description}
            </p>
          </div>

          <div className="bg-[#1a1447]/80 backdrop-blur rounded-2xl p-8 border border-slate-800 mb-12">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-2xl font-semibold text-white mb-2">{t.home.dashboard.heading}</h3>
                <p className="text-sm text-slate-400">{t.home.dashboard.subheading}</p>
              </div>
              <div className="flex items-center space-x-2 bg-purple-500/10 px-4 py-2 rounded-lg border border-purple-500/30">
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-purple-400">{t.home.dashboard.live}</span>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="relative overflow-hidden bg-gradient-to-br from-blue-600/10 via-purple-600/5 to-transparent backdrop-blur rounded-2xl p-6 border border-purple-500/30 hover:border-purple-400/60 hover:shadow-lg hover:shadow-purple-500/20 transition-all group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/20 to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform"></div>
                <div className="relative flex justify-between items-start mb-4">
                  <div>
                    <p className="text-slate-400 text-xs mb-1 font-semibold">{t.home.dashboard.projects.bucharest}</p>
                    <p className="text-3xl font-black bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">€87,450</p>
                  </div>
                  <div className="bg-emerald-500/20 text-emerald-400 text-xs font-bold px-3 py-1.5 rounded-lg border border-emerald-500/40">-12%</div>
                </div>
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-slate-400">{t.home.dashboard.costs.materials}</span>
                    <span className="font-semibold text-slate-200">€42,300</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">{t.home.dashboard.costs.labor}</span>
                    <span className="font-semibold text-slate-200">€31,150</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">{t.home.dashboard.costs.equipment}</span>
                    <span className="font-semibold text-slate-200">€14,000</span>
                  </div>
                </div>
                <div className="pt-3 border-t border-slate-700 flex items-center text-xs text-slate-500">
                  <Clock className="h-3 w-3 mr-1" />
                  {t.home.dashboard.updated.now2min}
                </div>
              </div>

              <div className="relative overflow-hidden bg-gradient-to-br from-violet-600/10 via-purple-600/5 to-transparent backdrop-blur rounded-2xl p-6 border border-violet-500/30 hover:border-violet-400/60 hover:shadow-lg hover:shadow-violet-500/20 transition-all group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-violet-500/20 to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform"></div>
                <div className="relative flex justify-between items-start mb-4">
                  <div>
                    <p className="text-slate-400 text-xs mb-1 font-semibold">{t.home.dashboard.projects.cluj}</p>
                    <p className="text-3xl font-black bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">€124,890</p>
                  </div>
                  <div className="bg-red-500/20 text-red-400 text-xs font-bold px-3 py-1.5 rounded-lg border border-red-500/40">+8%</div>
                </div>
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-slate-400">{t.home.dashboard.costs.materials}</span>
                    <span className="font-semibold text-slate-200">€68,200</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">{t.home.dashboard.costs.labor}</span>
                    <span className="font-semibold text-slate-200">€45,690</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">{t.home.dashboard.costs.equipment}</span>
                    <span className="font-semibold text-slate-200">€11,000</span>
                  </div>
                </div>
                <div className="pt-3 border-t border-slate-700 flex items-center text-xs text-slate-500">
                  <Clock className="h-3 w-3 mr-1" />
                  {t.home.dashboard.updated.now5min}
                </div>
              </div>

              <div className="relative overflow-hidden bg-gradient-to-br from-purple-600/10 via-blue-600/5 to-transparent backdrop-blur rounded-2xl p-6 border border-purple-500/30 hover:border-purple-400/60 hover:shadow-lg hover:shadow-purple-500/20 transition-all group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/20 to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform"></div>
                <div className="relative flex justify-between items-start mb-4">
                  <div>
                    <p className="text-slate-400 text-xs mb-1 font-semibold">{t.home.dashboard.projects.timisoara}</p>
                    <p className="text-3xl font-black bg-gradient-to-r from-purple-400 to-violet-400 bg-clip-text text-transparent">€56,720</p>
                  </div>
                  <div className="bg-emerald-500/20 text-emerald-400 text-xs font-bold px-3 py-1.5 rounded-lg border border-emerald-500/40">-23%</div>
                </div>
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-slate-400">{t.home.dashboard.costs.materials}</span>
                    <span className="font-semibold text-slate-200">€28,100</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">{t.home.dashboard.costs.labor}</span>
                    <span className="font-semibold text-slate-200">€19,620</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">{t.home.dashboard.costs.equipment}</span>
                    <span className="font-semibold text-slate-200">€9,000</span>
                  </div>
                </div>
                <div className="pt-3 border-t border-slate-700 flex items-center text-xs text-slate-500">
                  <Clock className="h-3 w-3 mr-1" />
                  {t.home.dashboard.updated.now1min}
                </div>
              </div>
            </div>

            <div className="bg-slate-800/30 rounded-xl p-6 border border-slate-700">
              <div className="flex items-start space-x-3 mb-4">
                <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 p-2 rounded-lg">
                  <BarChart3 className="h-5 w-5 text-purple-400" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white mb-1">{t.home.dashboard.liveTitle}</h4>
                  <p className="text-sm text-slate-400">{t.home.dashboard.autoUpdate}</p>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t.home.dashboard.features.timeTracking}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t.home.dashboard.features.fuel}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t.home.dashboard.features.gps}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-purple-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{t.home.dashboard.features.procurement}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-purple-50/30 to-violet-50/40"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center">
            <Link to={getLocalizedPath('/demo')}>
              <button className="relative overflow-hidden group bg-gradient-to-r from-blue-600 via-purple-600 to-violet-600 hover:from-violet-600 hover:via-purple-600 hover:to-blue-600 text-white px-10 py-4 rounded-lg font-semibold text-lg transition-all inline-flex items-center shadow-lg shadow-purple-500/50 hover:shadow-purple-500/70">
                {t.home.dashboard.cta}
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </Link>
            <p className="text-slate-600 text-sm mt-4 font-medium">{t.home.dashboard.ctaSubtitle}</p>
          </div>
        </div>
      </section>

      <section className="relative py-32 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0E0A31] via-[#1a1447] to-[#0E0A31]">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black mb-6 text-white">
              {t.home.mobileApp.title}
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-4">
              {t.home.mobileApp.subtitle}
            </p>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              {t.home.mobileApp.description}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur rounded-2xl p-8 border border-slate-700 hover:border-emerald-500/50 hover:shadow-xl hover:shadow-emerald-500/10 transition-all group">
              <div className="bg-gradient-to-br from-emerald-500 to-teal-500 w-14 h-14 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <Users className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-black text-white mb-3">{t.home.mobileApp.features.timeTracking.title}</h3>
              <p className="text-slate-400 leading-relaxed">{t.home.mobileApp.features.timeTracking.description}</p>
            </div>

            <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur rounded-2xl p-8 border border-slate-700 hover:border-blue-500/50 hover:shadow-xl hover:shadow-blue-500/10 transition-all group">
              <div className="bg-gradient-to-br from-blue-500 to-cyan-500 w-14 h-14 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <QrCode className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-black text-white mb-3">{t.home.mobileApp.features.equipment.title}</h3>
              <p className="text-slate-400 leading-relaxed">{t.home.mobileApp.features.equipment.description}</p>
            </div>

            <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur rounded-2xl p-8 border border-slate-700 hover:border-violet-500/50 hover:shadow-xl hover:shadow-violet-500/10 transition-all group">
              <div className="bg-gradient-to-br from-violet-500 to-purple-500 w-14 h-14 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <Camera className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-black text-white mb-3">{t.home.mobileApp.features.stock.title}</h3>
              <p className="text-slate-400 leading-relaxed">{t.home.mobileApp.features.stock.description}</p>
            </div>

            <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur rounded-2xl p-8 border border-slate-700 hover:border-amber-500/50 hover:shadow-xl hover:shadow-amber-500/10 transition-all group">
              <div className="bg-gradient-to-br from-amber-500 to-orange-500 w-14 h-14 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <ClipboardList className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-black text-white mb-3">{t.home.mobileApp.features.dailyProgram.title}</h3>
              <p className="text-slate-400 leading-relaxed">{t.home.mobileApp.features.dailyProgram.description}</p>
            </div>

            <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur rounded-2xl p-8 border border-slate-700 hover:border-teal-500/50 hover:shadow-xl hover:shadow-teal-500/10 transition-all group">
              <div className="bg-gradient-to-br from-teal-500 to-emerald-500 w-14 h-14 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <FileCheck className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-black text-white mb-3">{t.home.mobileApp.features.reports.title}</h3>
              <p className="text-slate-400 leading-relaxed">{t.home.mobileApp.features.reports.description}</p>
            </div>

            <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur rounded-2xl p-8 border border-slate-700 hover:border-pink-500/50 hover:shadow-xl hover:shadow-pink-500/10 transition-all group">
              <div className="bg-gradient-to-br from-pink-500 to-rose-500 w-14 h-14 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <MapPin className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-black text-white mb-3">{t.home.mobileApp.features.routeSheets.title}</h3>
              <p className="text-slate-400 leading-relaxed">{t.home.mobileApp.features.routeSheets.description}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-6 bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 bg-clip-text text-transparent">
              {t.home.easyToUse.title}
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              {t.home.easyToUse.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <Smile className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                {t.home.easyToUse.features.noTraining.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {t.home.easyToUse.features.noTraining.description}
              </p>
            </div>

            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <Rocket className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                {t.home.easyToUse.features.instantStart.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {t.home.easyToUse.features.instantStart.description}
              </p>
            </div>

            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-500 mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <Monitor className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                {t.home.easyToUse.features.everywhere.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {t.home.easyToUse.features.everywhere.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="relative py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-20">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-xl border border-purple-500/50 px-4 py-2 rounded-full mb-6 shadow-lg shadow-purple-500/30">
              <Star className="h-4 w-4 text-purple-400" />
              <span className="text-sm font-semibold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">{t.home.features.title}</span>
            </div>
            <h2 className="text-5xl font-black mb-6 text-white">
              {t.home.features.subtitle}
            </h2>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              {t.home.features.description}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link to={getLocalizedPath('/hr-pontaj')}>
              <PremiumFeatureCard
                icon={<Users className="h-7 w-7" />}
                title={t.home.features.hr.title}
                description={t.home.features.hr.description}
                gradient="from-blue-600 to-purple-600"
              />
            </Link>
            <Link to={getLocalizedPath('/logistica')}>
              <PremiumFeatureCard
                icon={<Truck className="h-7 w-7" />}
                title={t.home.features.logistics.title}
                description={t.home.features.logistics.description}
                gradient="from-purple-600 to-violet-600"
              />
            </Link>
            <Link to={getLocalizedPath('/achizitii')}>
              <PremiumFeatureCard
                icon={<Package className="h-7 w-7" />}
                title={t.home.features.procurement.title}
                description={t.home.features.procurement.description}
                gradient="from-violet-600 to-blue-600"
              />
            </Link>
            <Link to={getLocalizedPath('/echipamente')}>
              <PremiumFeatureCard
                icon={<Building2 className="h-7 w-7" />}
                title={t.home.features.equipment.title}
                description={t.home.features.equipment.description}
                gradient="from-emerald-600 to-green-600"
              />
            </Link>
            <Link to={getLocalizedPath('/documente')}>
              <PremiumFeatureCard
                icon={<FileText className="h-7 w-7" />}
                title={t.home.features.documents.title}
                description={t.home.features.documents.description}
                gradient="from-blue-600 to-indigo-600"
              />
            </Link>
            <PremiumFeatureCard
              icon={<BarChart3 className="h-7 w-7" />}
              title={t.home.features.analytics.title}
              description={t.home.features.analytics.description}
              gradient="from-violet-600 to-purple-600"
            />
            <PremiumFeatureCard
              icon={<Fuel className="h-7 w-7" />}
              title={t.home.features.fuel.title}
              description={t.home.features.fuel.description}
              gradient="from-orange-600 to-red-600"
            />
            <PremiumFeatureCard
              icon={<Clock className="h-7 w-7" />}
              title={t.home.features.costCenters.title}
              description={t.home.features.costCenters.description}
              gradient="from-pink-600 to-rose-600"
            />
            <PremiumFeatureCard
              icon={<Shield className="h-7 w-7" />}
              title={t.home.features.security.title}
              description={t.home.features.security.description}
              gradient="from-slate-600 to-gray-600"
            />
          </div>
        </div>
      </section>

      <section id="benefits" className="relative py-32 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-slate-50">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-violet-500/5"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4 bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 bg-clip-text text-transparent">
              {t.home.benefits.title}
            </h2>
            <p className="text-lg text-slate-700 max-w-2xl mx-auto font-medium">
              {t.home.benefits.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white/80 backdrop-blur rounded-2xl p-8 border border-slate-200 shadow-xl hover:shadow-2xl hover:border-blue-300 transition-all group">
              <div className="text-5xl font-black bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-3">{t.home.benefits.costReduction.percentage}</div>
              <p className="text-lg font-black text-slate-900 mb-2">{t.home.benefits.costReduction.title}</p>
              <p className="text-slate-600 text-sm font-semibold">{t.home.benefits.costReduction.description}</p>
            </div>
            <div className="bg-white/80 backdrop-blur rounded-2xl p-8 border border-slate-200 shadow-xl hover:shadow-2xl hover:border-violet-300 transition-all group">
              <div className="text-5xl font-black bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent mb-3">{t.home.benefits.efficiency.percentage}</div>
              <p className="text-lg font-black text-slate-900 mb-2">{t.home.benefits.efficiency.title}</p>
              <p className="text-slate-600 text-sm font-semibold">{t.home.benefits.efficiency.description}</p>
            </div>
            <div className="bg-white/80 backdrop-blur rounded-2xl p-8 border border-slate-200 shadow-xl hover:shadow-2xl hover:border-emerald-300 transition-all group">
              <div className="text-5xl font-black bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-3">{t.home.benefits.stock.percentage}</div>
              <p className="text-lg font-black text-slate-900 mb-2">{t.home.benefits.stock.title}</p>
              <p className="text-slate-600 text-sm font-semibold">{t.home.benefits.stock.description}</p>
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              <PremiumBenefitItem
                title={t.home.benefits.roi.title}
                description={t.home.benefits.roi.description}
              />
              <PremiumBenefitItem
                title={t.home.benefits.automation.title}
                description={t.home.benefits.automation.description}
              />
              <PremiumBenefitItem
                title={t.home.benefits.ai.title}
                description={t.home.benefits.ai.description}
              />
              <PremiumBenefitItem
                title={t.home.benefits.scale.title}
                description={t.home.benefits.scale.description}
              />
              <PremiumBenefitItem
                title={t.home.benefits.support.title}
                description={t.home.benefits.support.description}
              />
              <PremiumBenefitItem
                title={t.home.benefits.integrations.title}
                description={t.home.benefits.integrations.description}
              />
            </div>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="relative py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-20">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600/20 to-pink-600/20 backdrop-blur-xl border border-purple-500/50 px-4 py-2 rounded-full mb-6 shadow-lg shadow-purple-500/30">
              <Globe className="h-4 w-4 text-purple-400" />
              <span className="text-sm font-semibold text-purple-300">{t.home.howItWorks.title}</span>
            </div>
            <h2 className="text-5xl font-black mb-6 text-white">
              {t.home.howItWorks.subtitle}
            </h2>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              {t.home.howItWorks.description}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <PremiumStepCard
              number={t.home.howItWorks.step1.number}
              title={t.home.howItWorks.step1.title}
              description={t.home.howItWorks.step1.description}
              gradient="from-blue-600 to-cyan-600"
            />
            <PremiumStepCard
              number={t.home.howItWorks.step2.number}
              title={t.home.howItWorks.step2.title}
              description={t.home.howItWorks.step2.description}
              gradient="from-cyan-600 to-teal-600"
            />
            <PremiumStepCard
              number={t.home.howItWorks.step3.number}
              title={t.home.howItWorks.step3.title}
              description={t.home.howItWorks.step3.description}
              gradient="from-teal-600 to-emerald-600"
            />
          </div>
        </div>
      </section>

      <section className="relative py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="relative overflow-hidden rounded-3xl">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 opacity-90"></div>
            <div className="absolute inset-0">
              <div className="absolute top-0 left-0 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute bottom-0 right-0 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse delay-700"></div>
            </div>
            <div className="relative p-16 text-center">
              <Lock className="h-16 w-16 text-white/80 mx-auto mb-6" />
              <h2 className="text-5xl font-black text-white mb-6">
                {t.home.cta.title}
              </h2>
              <p className="text-2xl text-blue-100 mb-10 max-w-2xl mx-auto">
                {t.home.cta.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Link to={getLocalizedPath('/demo')}>
                  <button className="group relative overflow-hidden px-10 py-6 rounded-xl font-bold text-xl shadow-2xl transform hover:scale-105 transition-all bg-white text-blue-600">
                    <span className="relative z-10 flex items-center justify-center">
                      {t.home.cta.button}
                      <Sparkles className="ml-2 h-5 w-5" />
                    </span>
                  </button>
                </Link>
                <Link to={getLocalizedPath('/contact')}>
                  <button className="group px-10 py-6 rounded-xl font-bold text-xl border-2 border-white text-white hover:bg-white hover:text-blue-600 transition-all flex items-center justify-center">
                    {t.home.cta.buttonAlt}
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </Link>
              </div>
              <p className="mt-8 text-blue-100 text-sm">
                {t.home.cta.guarantee}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0E0A31] via-[#1a1447] to-[#0E0A31]">
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              {t.home.faq.title}
            </h2>
            <p className="text-lg text-slate-300">
              {t.home.faq.subtitle}
            </p>
          </div>

          <div className="space-y-6">
            {t.home.faq.questions?.map((faqItem, index) => (
              <FAQItem
                key={index}
                question={faqItem.question}
                answer={faqItem.answer}
              />
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="text-slate-300 mb-6">{t.home.faq.contact}</p>
            <Link to={getLocalizedPath('/contact')}>
              <button className="group px-8 py-4 rounded-xl font-semibold text-lg border-2 border-blue-500 text-blue-400 hover:bg-blue-500 hover:text-white transition-all inline-flex items-center">
                {t.home.faq.contactButton}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

interface FAQItemProps {
  question: string;
  answer: string;
}

function FAQItem({ question, answer }: FAQItemProps) {
  return (
    <details className="group bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur rounded-xl border border-slate-700 hover:border-blue-500/50 transition-all overflow-hidden">
      <summary className="cursor-pointer p-6 font-semibold text-white text-lg flex justify-between items-center list-none">
        <span className="pr-8">{question}</span>
        <ChevronRight className="h-5 w-5 text-slate-400 group-open:rotate-90 transition-transform flex-shrink-0" />
      </summary>
      <div className="px-6 pb-6 text-slate-300 leading-relaxed border-t border-slate-700/50 pt-4">
        {answer}
      </div>
    </details>
  );
}

function StatCard({ number, label, icon }: { number: string; label: string; icon: React.ReactNode }) {
  return (
    <div className="bg-[#1a1447]/50 backdrop-blur rounded-lg p-4 border border-slate-800 hover:border-slate-700 transition-all group">
      <div className="flex items-center space-x-2 mb-2 text-blue-500">
        {icon}
        <div className="text-xl font-bold">{number}</div>
      </div>
      <div className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors">{label}</div>
    </div>
  );
}

function ProjectCard({ name, progress, color }: { name: string; progress: number; color: string }) {
  const colorMap: Record<string, string> = {
    emerald: 'bg-emerald-500',
    blue: 'bg-blue-500',
    amber: 'bg-amber-500'
  };

  return (
    <div className="bg-[#1a1447]/50 rounded-xl p-4 border border-white/5 hover:border-white/20 transition-all">
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm font-semibold">{name}</span>
        <span className={`text-sm font-bold ${color === 'emerald' ? 'text-emerald-400' : color === 'blue' ? 'text-blue-400' : 'text-amber-400'}`}>
          {progress}%
        </span>
      </div>
      <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden">
        <div className={`h-2 rounded-full ${colorMap[color]} transition-all duration-500`} style={{ width: `${progress}%` }}></div>
      </div>
    </div>
  );
}

function MetricBox({ label, value, trend }: { label: string; value: string; trend: string }) {
  const isPositive = trend.startsWith('+');
  return (
    <div className="bg-[#1a1447]/50 rounded-xl p-4 border border-white/5">
      <div className="text-xs text-gray-400 mb-1">{label}</div>
      <div className="text-xl font-bold">{value}</div>
      <div className={`text-xs font-semibold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>{trend}</div>
    </div>
  );
}

function PremiumFeatureCard({ icon, title, description, gradient }: { icon: React.ReactNode; title: string; description: string; gradient: string }) {
  return (
    <div className="group relative">
      <div className="absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500"></div>
      <div className={`relative bg-gradient-to-br ${gradient} bg-opacity-5 backdrop-blur p-6 rounded-2xl border border-slate-700/50 hover:border-slate-600 hover:shadow-lg transition-all`}>
        <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${gradient} bg-opacity-10 mb-4 shadow-lg`}>
          <div className="text-white">{icon}</div>
        </div>
        <h3 className="text-lg font-bold mb-2 text-white">{title}</h3>
        <p className="text-slate-300 leading-relaxed text-sm">{description}</p>
      </div>
    </div>
  );
}

function PremiumBenefitItem({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex items-start space-x-4 group">
      <div className="flex-shrink-0">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-green-600 rounded-full blur-lg opacity-30"></div>
          <CheckCircle className="relative h-7 w-7 text-emerald-600" />
        </div>
      </div>
      <div>
        <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-emerald-600 transition-colors">{title}</h3>
        <p className="text-gray-600 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

function PremiumStepCard({ number, title, description, gradient }: { number: string; title: string; description: string; gradient: string }) {
  return (
    <div className="relative group">
      <div className="absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500"></div>
      <div className={`relative bg-gradient-to-br ${gradient} bg-opacity-5 backdrop-blur p-8 rounded-2xl border border-slate-700/50 hover:border-slate-600 hover:shadow-xl transition-all text-center`}>
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br ${gradient} text-white text-2xl font-black mb-6 shadow-lg`}>
          {number}
        </div>
        <h3 className="text-xl font-bold mb-3 text-white">{title}</h3>
        <p className="text-slate-300 leading-relaxed text-sm">{description}</p>
      </div>
    </div>
  );
}
